import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild, computed, inject, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IBizEntity, IRights, LoggerService } from '@optima-stride-ai/core';
import { BizEntityStateStoreService, MarkdownWrapperComponent } from '@optima-stride-ai/optima-app-framework';
import {
  MarkdownCollaborationAdapter,
  MarkdownViewMode,
  ProcessProgressView,
  ProgressIndicatorComponent,
  ShimmerVariant,
  ToolbarInput
} from '@optima-stride-ai/optima-components';
import { UserContextService } from '@optima-stride-ai/optima-platform';
import { Subscription } from 'rxjs';
import { AppStateService } from '../../../services/state-store/app-state';
import { EntityLayoutConfigService } from '../../../services/entity-layout-config.service';
import { GlobalStateService } from '../../../../service/global.service';
import { YjsCollaborationService } from '../services/yjs-collaboration.service';
import { MarkdownAutosaveInterval } from '../../../../enum/markdown-autosave-interval';

@Component({
  selector: 'app-markdown-editor',
  imports: [CommonModule, MarkdownWrapperComponent, ProgressIndicatorComponent],
  templateUrl: './markdown-editor.component.html',
  styleUrls: ['./markdown-editor.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MarkdownEditorComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly ProcessProgressView = ProcessProgressView;
  readonly ShimmerVariant = ShimmerVariant;

  /** Route-provided business entity/file identifier; required for loading. */
  id = input<string>();
  /** Host-provided rights used to select edit or view mode. */
  rights = input<IRights>();
  /** Optional prepared iframe metadata avoids fetching the complete entity list. */
  entity = input<IBizEntity>();

  private readonly bizEntityStateStoreService = inject(BizEntityStateStoreService);
  private readonly logger = inject(LoggerService);
  private readonly yjsCollaboration = inject(YjsCollaborationService);
  private readonly userContextService = inject(UserContextService);
  private readonly entityLayoutConfigService = inject(EntityLayoutConfigService);
  private readonly globalStateService = inject(GlobalStateService);
  private readonly appStateService = inject(AppStateService);

  @ViewChild(MarkdownWrapperComponent) markdownWrapper?: MarkdownWrapperComponent;

  /** Resolved entity metadata used for naming and collaboration. */
  selectedBizEntity?: IBizEntity;
  /** Tracks whether the Framework has delivered content. */
  isDataLoaded = signal(false);
  /** Drives the consumer-owned loading shimmer. */
  isLoading = signal(true);
  /** Controls consumer error presentation. */
  hasError = signal(false);
  /** Error text received from the Framework load path. */
  errorMessage = signal('');
  /** Latest content received from the wrapper. */
  editorContent = signal('');
  private isLoadInProgress = false;
  private saveRequestSubscription?: Subscription;

  readonly MarkdownViewMode = MarkdownViewMode;
  readonly MarkdownAutosaveInterval = MarkdownAutosaveInterval;
  /** Hayagriva toolbar configuration passed to the wrapper. */
  readonly markdownToolbar: ToolbarInput = {
    extend: true,
    items: ['table', 'horizontal-rule', 'guide', 'undo', 'redo']
  };

  /** Hayagriva Yjs adapter passed to the reusable wrapper. */
  readonly collaborationAdapter: MarkdownCollaborationAdapter = {
    onlineUsers: this.yjsCollaboration.onlineUsers,
    currentUser: this.yjsCollaboration.currentUser,
    connectionStatus: this.yjsCollaboration.connectionStatus,
    connect: (content, onRemoteContent) => {
      const entity = this.selectedBizEntity || ({
        id: String(this.id() || ''),
        name: 'Markdown document'
      } as IBizEntity);
      if (!entity.id) return false;
      this.yjsCollaboration.connect(entity, this.userContextService.userName, this.generateRandomColor(), content, onRemoteContent);
      return true;
    },
    updateContent: (content) => this.yjsCollaboration.updateContent(content),
    updateSelection: (start, end) => this.yjsCollaboration.updateSelection(start, end),
    clearSelection: () => this.yjsCollaboration.clearSelection(),
    disconnect: () => this.yjsCollaboration.disconnect()
  };

  /** Derives editability from the host-provided rights. */
  isReadOnly = computed(() => this.rights()?.update !== true);

  /** Subscribes to application-wide save requests. */
  ngOnInit(): void {
    this.saveRequestSubscription = this.globalStateService.saveRequested$.subscribe((payload) => {
      const id = this.id();
      if (payload && id && (!payload.entityId || payload.entityId === id) && !this.isReadOnly()) {
        void this.markdownWrapper?.save(this.getContent());
      }
    });
  }

  /** Starts entity preparation and delegates loading to the Framework wrapper. */
  ngAfterViewInit(): void {
    setTimeout(() => { const id = this.id(); if (id) void this.loadMarkdownFile(id); }, 100);
  }

  /** Clears Hayagriva entity and collaboration state. */
  ngOnDestroy(): void {
    const entityId = this.id();
    if (entityId) this.entityLayoutConfigService.clearEntityCache(entityId);
    this.saveRequestSubscription?.unsubscribe();
    this.yjsCollaboration.disconnect();
  }

  /** Forwards wrapper edits to Hayagriva's Yjs service. */
  onEditorContentChange(content: string): void {
    this.editorContent.set(content);
    this.yjsCollaboration.updateContent(content);
  }

  /** Handles the consumer-specific upload action. */
  onFileUploadClick(): void {
    this.logger.info('Markdown file upload requested');
  }

  /** Receives normalized content from the Framework wrapper. */
  onContentLoaded(content: string): void {
    const resolved = content.trim() ? content : '# Welcome to Markdown Editor\n\nStart typing your content here...';
    this.editorContent.set(resolved);
    this.isDataLoaded.set(true);
    this.isLoading.set(false);
    this.hasError.set(false);
  }

  /** Receives Framework load failures and updates consumer state. */
  onContentLoadFailed(error: unknown): void {
    this.isLoading.set(false);
    this.hasError.set(true);
    this.errorMessage.set(error instanceof Error ? error.message : String(error));
    this.isDataLoaded.set(true);
  }

  /** Retries loading the current route-selected document. */
  retryLoad(): void {
    const id = this.id();
    if (!id) return;
    this.isLoading.set(true);
    this.hasError.set(false);
    void this.loadMarkdownFile(id);
  }

  /** Reads current content through the Framework wrapper. */
  getContent(): string {
    return this.markdownWrapper?.getValue() || this.editorContent();
  }

  /** Sets content through the Framework wrapper. */
  setContent(content: string): void {
    this.editorContent.set(content);
    this.markdownWrapper?.setContent(content);
  }

  /** Resolves Hayagriva metadata, then delegates Markdown loading to the Framework. */
  private async loadMarkdownFile(id: string): Promise<void> {
    if (this.isLoadInProgress || (this.isDataLoaded() && this.selectedBizEntity?.id === id)) return;
    this.isLoadInProgress = true;
    try {
      const iframeEntity = this.entity();
      if (iframeEntity && String(iframeEntity.id) === String(id)) {
        this.selectedBizEntity = iframeEntity;
        await this.markdownWrapper?.load();
        return;
      }
      const selectedFromAppState = this.appStateService.selectedBizEntityForEdit;
      let entities: IBizEntity[] = [];
      if (selectedFromAppState?.id !== id) {
        try {
          entities = (await this.bizEntityStateStoreService.getState()) || [];
        } catch (error) {
          this.logger.warn(`Unable to resolve Markdown entity metadata; continuing with file ID: ${error}`);
        }
      }
      this.selectedBizEntity = (selectedFromAppState?.id === id ? selectedFromAppState : undefined) ||
        entities.find((entity) => entity?.id === id) ||
        ({ id, name: selectedFromAppState?.name || `Document-${id}` } as IBizEntity);
      await this.markdownWrapper?.load();
    } catch (error) {
      this.logger.error(`Failed to load markdown document: ${error}`);
      this.onContentLoadFailed(error);
    } finally {
      this.isLoadInProgress = false;
    }
  }

  /** Generates the Hayagriva-specific Yjs presence color. */
  private generateRandomColor(): string {
    const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E2'];
    return colors[Math.floor(Math.random() * colors.length)];
  }
}
